import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { AdminReturnRow } from '@terra/api-client';
import { Badge, Spinner } from '@terra/ui';

import { api } from '../lib/api';

/**
 * Returns that need Terra Shoppy: ones waiting for approval, ones a seller
 * disputed after inspection, and ones being inspected (to watch deadlines).
 * Each row opens its order, where the approve / reject / refund decisions are.
 */
const TABS: Array<{ status: string; label: string; hint: string }> = [
  {
    status: 'REQUESTED',
    label: 'Awaiting approval',
    hint: 'Not auto-approved: high value or a frequent returner. Approve to book the pickup.',
  },
  {
    status: 'QC_FAILED',
    label: 'Seller disputes',
    hint: "The seller reported a problem after inspection. Compare both sides' photos and decide.",
  },
  {
    status: 'QC_PENDING',
    label: 'In inspection',
    hint: 'Back with the seller. Refunded automatically when the inspection window ends.',
  },
];

export function ReturnQueue() {
  const [status, setStatus] = useState('QC_FAILED');
  const counts = useQuery({
    queryKey: ['admin-return-queue-counts'],
    queryFn: async () => {
      const results = await Promise.all(
        TABS.map((t) => api.orders.adminReturns({ status: t.status, pageSize: 1 })),
      );
      return Object.fromEntries(TABS.map((t, i) => [t.status, results[i]!.page.total]));
    },
  });
  const list = useQuery({
    queryKey: ['admin-return-queue', status],
    queryFn: () => api.orders.adminReturns({ status, pageSize: 50 }),
  });
  const tab = TABS.find((t) => t.status === status)!;

  return (
    <section className="border-border/60 bg-surface rounded-2xl border p-4 shadow-sm">
      <h2 className="text-ink text-sm font-bold uppercase tracking-wider">Return queue</h2>
      <div className="mt-3 flex flex-wrap gap-2">
        {TABS.map((t) => {
          const n = counts.data?.[t.status];
          const active = t.status === status;
          return (
            <button
              key={t.status}
              type="button"
              onClick={() => setStatus(t.status)}
              className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${
                active
                  ? 'border-[#a8161b] bg-[#a8161b] text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-[#a8161b]'
              }`}
            >
              {t.label}
              {n !== undefined && n > 0 ? ` (${n})` : ''}
            </button>
          );
        })}
      </div>
      <p className="text-ink-muted mt-2 text-xs">{tab.hint}</p>

      <div className="mt-3">
        {list.isLoading ? (
          <div className="grid min-h-[80px] place-items-center">
            <Spinner />
          </div>
        ) : (list.data?.data ?? []).length === 0 ? (
          <p className="text-ink-muted rounded-xl border border-dashed border-slate-200 px-3 py-6 text-center text-sm">
            Nothing here right now.
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-100">
            {(list.data?.data ?? []).map((r) => (
              <ReturnRow key={r.id} r={r} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function ReturnRow({ r }: { r: AdminReturnRow }) {
  const items = r.items
    .map((it) => `${it.lineItem.productTitle}${it.qty > 1 ? ` × ${it.qty}` : ''}`)
    .join(', ');
  return (
    <li>
      <Link
        to={`/orders/${r.subOrder.orderGroup.id}`}
        className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5 hover:bg-slate-50"
      >
        <div className="min-w-0">
          <p className="text-sm font-bold text-slate-800">
            #{r.subOrder.orderGroup.orderNumber}{' '}
            <span className="text-xs font-semibold text-slate-500">
              · {r.subOrder.vendor.storeName}
              {r.subOrder.orderGroup.customer?.name
                ? ` · ${r.subOrder.orderGroup.customer.name}`
                : ''}
            </span>
          </p>
          <p className="truncate text-xs text-slate-600">{items}</p>
          <p className="truncate text-xs text-slate-500">
            {r.status === 'QC_FAILED' && r.qcNote ? `Seller: ${r.qcNote}` : `Reason: ${r.reason}`}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {r.photoUrls.length + r.qcPhotoUrls.length > 0 && (
            <Badge tone="info">{r.photoUrls.length + r.qcPhotoUrls.length} photos</Badge>
          )}
          {r.status === 'QC_PENDING' && r.qcDeadlineAt && (
            <span className="text-xs text-slate-500">
              until{' '}
              {new Date(r.qcDeadlineAt).toLocaleString('en-IN', {
                day: 'numeric',
                month: 'short',
                hour: 'numeric',
              })}
            </span>
          )}
          <span className="text-xs font-bold text-[#a8161b]">Open →</span>
        </div>
      </Link>
    </li>
  );
}
